// frontend/src/lib/apiClient.ts — central HTTP client (Axios).
//
// One instance for the whole app. Per CONVENTIONS §Frontend state +
// ADR-003 §Revocation propagation + ADR-010 (Axios):
//
//   - withCredentials: true — session cookie travels on every request.
//     Backend issues a __Host-prefixed cookie; no Bearer token, no JWT, no
//     refresh flow (ADR-003).
//   - timeout: 30s — prevents hung requests from blocking the UI forever.
//   - Response interceptor branches:
//       401: broadcast `auth.logout` (Layer 3) + clear queryClient +
//            redirect to /login. Login + the bootstrap /me probe are
//            EXEMPT from redirect (their 401 is the legitimate "not
//            authenticated yet" answer); their errors bubble to the caller.
//       403: branch ONLY on known codes (MUST_CHANGE_PASSWORD /
//            PROJECT_SUSPENDED / PROJECT_TERMINATED). Unknown 403 codes
//            fall through to the caller — TanStack Query renders the error
//            state. The dev branch's redirect-to-/home catch-all was a
//            footgun and is NOT carried forward.
//       404: on a READ, a typed `*_NOT_FOUND` code routes the user to
//            /not-found with the code in router state; an untyped 404 routes
//            there with RESOURCE_NOT_FOUND. (Generalizes per FRONTEND-PATTERNS
//            §Resolver chain.) Two never navigate: a read marked
//            `quietNotFound` (a BACKGROUND read never takes the person off
//            their page — its caller renders the 404), and ANY WRITE — a row
//            deleted under an open form is said in the form, by its own
//            line (ADR-084 §6); the caller renders it.
//       503 BUSY: the fuse (ADR-079 §5) — the server is full, not broken.
//            On the person's OWN act (a page open, a save, an export) it is
//            ONE calm toast, keyed so a burst never stacks; the caller still
//            receives the error (a form shows nothing more for it; TanStack
//            retries once after the server's hint — lib/queryClient.ts). A
//            question the TAB asks by itself (`quietBusy` — the glance, the
//            session's own check, the live client's look-agains) says
//            nothing: busy is an answer those callers handle themselves.
//            Told apart by the body's CODE, never the status (lib/refusal.ts):
//            a proxy's HTML 503 is not a busy answer.
//   - A FILE request (`responseType: 'blob'`) receives a refusal's JSON body
//     as a Blob: the interceptor reads it back into the envelope BEFORE any
//     branch, so an export's refusal is judged like any other — by its code.
//   - Every successful WRITE on a project URL (any method but GET / HEAD /
//     OPTIONS) is told to the live client, which asks the server what changed
//     at once instead of waiting for its next glance (ADR-074 §1). Here, at the
//     one door every write passes — an upload is three raw calls no mutation
//     ever sees. A POST that is really a READ (a signed-link mint, an upload's
//     announce — routes the server declares as moving nothing a screen shows)
//     says so with `movesNothing`, or scrolling a photo wall would ask a
//     "what changed?" per batch of thumbnails.
//
// When the router has not plugged in a `navigate()` callback, the
// interceptor uses `window.location.href` (hard navigation) — sufficient
// for the auth flows the interceptor governs (401 = full reset is the right
// posture anyway).
//
// No `X-Active-Project` mismatch branch — ADR-016 puts project context in
// the URL, mismatch is structurally impossible. Do not add.

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { apiUrl } from '../config.js';
import { broadcastAuth } from './authBroadcast.js';
import { isBusy, retryAfterOf } from './refusal.js';

// Paths exempt from the 401 redirect — the FE expects 401 as a legitimate
// "not authenticated yet" answer and bubbles it to the caller. Note: these
// are paths RELATIVE TO baseURL (which already includes any `/api` prefix
// per env). Code MUST NOT include `/api` in apiClient call URLs.
const AUTH_EXEMPT_PATHS = ['/auth/login', '/auth/me'];

declare module 'axios' {
  interface AxiosRequestConfig {
    /** A read whose caller says a 404 itself — the live client re-asking a
     *  question already on screen, a form's Load the latest: a 404 never
     *  navigates, the caller renders it. */
    quietNotFound?: boolean;
    /** A question the tab asks by itself (the glance, the session's own check,
     *  a live look-again) — never the person's act: a BUSY answer raises no
     *  toast, the caller handles it. */
    quietBusy?: boolean;
    /** A non-GET that changes nothing a screen shows (the server declares the
     *  route `moves: none`): not "the person's own write" — no glance is asked. */
    movesNothing?: boolean;
  }
}

interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
  request_id?: string;
}

interface NavigateOptions {
  state?: { errorCode?: string };
  replace?: boolean;
}

type NavigateFn = (to: string, opts?: NavigateOptions) => void;

// The router wires in a real `navigate()` (from React Router 7) by calling
// `setNavigate(fn)` once at app boot. Until then the fallback hits
// window.location directly.
let injectedNavigate: NavigateFn | null = null;

export function setNavigate(fn: NavigateFn | null): void {
  injectedNavigate = fn;
}

function go(to: string, opts?: NavigateOptions): void {
  if (injectedNavigate) {
    injectedNavigate(to, opts);
    return;
  }
  if (typeof window === 'undefined') return;
  // Hard navigation fallback. `opts.state` is lost on full reload; the
  // router preserves it.
  window.location.href = to;
}

// queryClient.clear() is a forward dependency. To avoid a circular import
// at module init (apiClient ↔ queryClient), the queryClient registers itself
// via setClearQueryCache(...) when it constructs.
let clearQueryCache: () => void = () => {};

export function setClearQueryCache(fn: () => void): void {
  clearQueryCache = fn;
}

// The live client registers itself the same way (lib/live) — apiClient never
// imports it.
let onProjectWrite: (projectId: string) => void = () => {};

export function setOnProjectWrite(fn: ((projectId: string) => void) | null): void {
  onProjectWrite = fn ?? (() => {});
}

// The busy answer's one toast is the shell's (it has the words and the store);
// registered the same way — apiClient never imports a store or i18n.
let onBusy: (retryAfterS: number | null) => void = () => {};

export function setOnBusy(fn: ((retryAfterS: number | null) => void) | null): void {
  onBusy = fn ?? (() => {});
}

/** A file request (`responseType: 'blob'`) receives a refusal's JSON body as a
 *  Blob: read it back into the envelope, so every branch below and every
 *  caller judges the refusal by its code. Only a JSON body is read — a proxy's
 *  HTML page stays what it is (no code: not a busy answer). */
async function readBlobEnvelope(error: AxiosError<ApiErrorBody>): Promise<void> {
  const response = error.response;
  const body: unknown = response?.data;
  if (!response || !(body instanceof Blob)) return;
  const type = body.type || String((response.headers as Record<string, unknown> | undefined)?.['content-type'] ?? '');
  if (!type.includes('application/json')) return;
  try {
    response.data = JSON.parse(await body.text()) as ApiErrorBody;
  } catch {
    // Not an envelope after all — left as it came.
  }
}

const READ_METHODS = new Set(['get', 'head', 'options']);

/** The project a request URL belongs to (URLs are relative to `baseURL`) — `null` outside the project trust zone. */
export function projectIdOfUrl(url: string | undefined): string | null {
  return /^\/?projects\/([^/?#]+)\//.exec(url ?? '')?.[1] ?? null;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  timeout: 30_000,
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => config,
  (err: unknown) => Promise.reject(err),
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const projectId = projectIdOfUrl(response.config.url);
    if (projectId && !response.config.movesNothing && !READ_METHODS.has((response.config.method ?? 'get').toLowerCase())) onProjectWrite(projectId);
    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    await readBlobEnvelope(error);
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const requestPath = error.config?.url ?? '';

    // 401 — session missing / revoked.
    if (status === 401) {
      const exempt = AUTH_EXEMPT_PATHS.some((p) => requestPath.startsWith(p) || requestPath.endsWith(p));
      if (!exempt) {
        // Layer 3 fan-out FIRST so sibling tabs converge in ~0s instead of
        // waiting for their own /me poll to surface a 401 up to 60s later.
        // Same-tab echo is suppressed by the BroadcastChannel spec.
        broadcastAuth({ type: 'logout' });
        clearQueryCache();
        if (typeof window === 'undefined' || !window.location.pathname.startsWith('/login')) {
          go('/login', { replace: true });
        }
      }
      return Promise.reject(error);
    }

    // 403 — branch only on known codes; everything else falls through to caller.
    if (status === 403) {
      if (code === 'MUST_CHANGE_PASSWORD') {
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/change-password')) {
          go('/change-password');
        }
      } else if (
        code === 'PROJECT_SUSPENDED' ||
        code === 'PROJECT_TERMINATED' ||
        code === 'TRIAL_EXPIRED'
      ) {
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/project-blocked')) {
          const reason =
            code === 'PROJECT_SUSPENDED'
              ? 'suspended'
              : code === 'PROJECT_TERMINATED'
                ? 'terminated'
                : 'trial-expired';
          // Carry the project id (parsed from the failing request URL) so the
          // blocked page can name the context in the switcher.
          const pid = /\/projects\/([^/?]+)/.exec(error.config?.url ?? '')?.[1];
          go(`/project-blocked?reason=${reason}${pid ? `&project=${pid}` : ''}`);
        }
      }
      // Unknown 403 codes (e.g. permission failures on specific actions):
      // do NOT redirect — let the caller / TanStack Query render the error.
      return Promise.reject(error);
    }

    // 404 — a READ's typed `*_NOT_FOUND` (or untyped) routes to the generic NotFound page; a background read and
    // every write reach their caller.
    if (status === 404) {
      if (error.config?.quietNotFound || !READ_METHODS.has((error.config?.method ?? 'get').toLowerCase())) return Promise.reject(error);
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/not-found')) {
        const errorCode = code ?? 'RESOURCE_NOT_FOUND';
        go('/not-found', { state: { errorCode } });
      }
      return Promise.reject(error);
    }

    // 503 BUSY — the fuse (ADR-079 §5): the person's own act meets ONE calm
    // toast; a question the tab asks by itself says nothing. The error reaches
    // the caller either way.
    if (isBusy(error) && !error.config?.quietBusy) onBusy(retryAfterOf(error));

    return Promise.reject(error);
  },
);
